import { cn } from '@/helpers/classnames';
import React from 'react';
import { Tooltip } from 'react-tooltip';
import Button from './Button';

export interface ToggleButtonOption {
  value: string;
  label?: string;
  labelClassName?: string;
  tooltip?: string;
  icon?: React.ReactNode;
}

interface ToggleButtonGroupProps {
  id: string;
  options: ToggleButtonOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  loadingValue?: string;
  labelledBy?: string;
}

const ToggleButtonGroup: React.FC<ToggleButtonGroupProps> = ({
  id,
  options,
  value,
  onChange,
  disabled,
  className,
  loadingValue,
  labelledBy,
}) => {
  const isLoading = loadingValue !== undefined;

  return (
    <div
      className="flex"
      role="group"
      aria-label={labelledBy ? undefined : id}
      aria-labelledby={labelledBy}
    >
      <Tooltip id={id} delayShow={300} delayHide={1} />
      {options.map((option) => (
        <Button
          data-tooltip-id={id}
          data-tooltip-content={option.tooltip}
          key={option.value}
          className={cn(
            'rounded-none first:rounded-l-md first:rounded-r-none last:rounded-l-none last:rounded-r-md',
            value === option.value &&
              'bg-primary-500 !text-contrast-primary hover:bg-primary-600',
            className,
          )}
          onClick={() => (disabled ? undefined : onChange(option.value))}
          disabled={disabled || isLoading}
          loading={loadingValue === option.value}
          aria-pressed={value === option.value}
          aria-label={option.label || option.tooltip}
        >
          <div className="flex grow items-center justify-center gap-1">
            {option.icon && loadingValue !== option.value && (
              <div aria-hidden="true">{option.icon}</div>
            )}
            {option.label && (
              <div className={option.labelClassName}>{option.label}</div>
            )}
          </div>
        </Button>
      ))}
    </div>
  );
};

export default ToggleButtonGroup;
